import React, { useMemo } from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import type { SurveyVideoProps } from "../../lib/schema";
import type { StatData } from "../../types/stats";
import { TEXT_PRIMARY, TEXT_SECONDARY, getCityColorMap } from "../../lib/colors";
import { getAmount } from "../../lib/animation";
import { resolveCities } from "../../lib/selection";

type OutroProps = Pick<SurveyVideoProps, "itemX" | "itemY" | "cities"> & {
  data: StatData;
};

const SPRING_CONFIG = { damping: 14, stiffness: 100, mass: 0.8 } as const;

type CitySummary = {
  city: string;
  color: string;
  xStart: number | null;
  xEnd: number | null;
  yStart: number | null;
  yEnd: number | null;
};

/** アウトロシーン (750-900f / 5s): サマリー表示 */
const Outro: React.FC<OutroProps> = ({ itemX, itemY, cities, data }) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const allCities = useMemo(
    () => resolveCities(cities, data.cities),
    [cities, data.cities],
  );
  const many = allCities.length > 3;
  const colorMap = useMemo(() => getCityColorMap(allCities), [allCities]);

  const startYear = data.years[0];
  const endYear = data.years[data.years.length - 1];

  // ── サマリーデータ ──────────────────────────────────
  const summaries: CitySummary[] = useMemo(() => {
    return allCities.map((city) => ({
      city,
      color: colorMap.get(city) ?? "#888",
      xStart: getAmount(data, startYear, city, itemX),
      xEnd: getAmount(data, endYear, city, itemX),
      yStart: getAmount(data, startYear, city, itemY),
      yEnd: getAmount(data, endYear, city, itemY),
    }));
  }, [allCities, colorMap, data, startYear, endYear, itemX, itemY]);

  // ── アニメーション ──────────────────────────────────
  const enterTitle = spring({ frame, fps, config: SPRING_CONFIG, durationInFrames: 60 });
  const enterContent = spring({ frame: frame - 15, fps, config: SPRING_CONFIG, durationInFrames: 60 });

  const fadeOut = interpolate(
    frame,
    [durationInFrames - 30, durationInFrames],
    [1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
  );

  return (
    <AbsoluteFill
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Segoe UI', Meiryo, 'Noto Sans JP', sans-serif",
        gap: 24,
        opacity: fadeOut,
      }}
    >
      {/* タイトル */}
      <div
        style={{
          fontSize: 58,
          fontWeight: 700,
          color: TEXT_PRIMARY,
          textShadow: "0 0 30px rgba(100,180,255,0.3)",
          transform: `translateY(${(1 - enterTitle) * 40}px)`,
          opacity: enterTitle,
        }}
      >
        {startYear} → {endYear} 推移まとめ
      </div>
      <div
        style={{
          fontSize: 38,
          color: TEXT_SECONDARY,
          transform: `translateY(${(1 - enterTitle) * 30}px)`,
          opacity: enterTitle,
        }}
      >
        {itemX} × {itemY}
      </div>

      {/* サマリーコンテンツ */}
      <div
        style={{
          marginTop: 24,
          transform: `translateY(${(1 - enterContent) * 30}px)`,
          opacity: enterContent,
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: many ? 12 : 24,
          maxWidth: 1600,
          padding: "0 40px",
        }}
      >
        {many ? (
          <RankingView summaries={summaries} itemX={itemX} itemY={itemY} />
        ) : (
          summaries.map((s) => <CityCard key={s.city} summary={s} itemX={itemX} itemY={itemY} />)
        )}
      </div>
    </AbsoluteFill>
  );
};

// ── ≤3 都市: カード表示 ─────────────────────────────

const CityCard: React.FC<{
  summary: CitySummary;
  itemX: string;
  itemY: string;
}> = ({ summary, itemX, itemY }) => {
  const { city, color, xStart, xEnd, yStart, yEnd } = summary;

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.05)",
        borderRadius: 16,
        padding: "24px 32px",
        border: `1px solid ${color}44`,
        minWidth: 320,
      }}
    >
      <div style={{ fontSize: 34, fontWeight: 700, color, marginBottom: 16 }}>
        {city}
      </div>
      <AmountRow label={itemX} start={xStart} end={xEnd} />
      <AmountRow label={itemY} start={yStart} end={yEnd} />
    </div>
  );
};

const AmountRow: React.FC<{
  label: string;
  start: number | null;
  end: number | null;
}> = ({ label, start, end }) => {
  const diff = start !== null && end !== null ? end - start : null;
  const diffColor = diff !== null ? (diff >= 0 ? "#4ade80" : "#f87171") : "#888";
  const fmt = (n: number | null) => (n !== null ? `${n.toLocaleString("ja-JP")}円` : "—");

  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 8 }}>
      <span style={{ fontSize: 24, color: TEXT_SECONDARY }}>{label}</span>
      <span style={{ fontSize: 24, color: TEXT_PRIMARY }}>
        {fmt(start)} → {fmt(end)}
        {diff !== null && (
          <span style={{ color: diffColor, marginLeft: 8, fontSize: 22 }}>
            ({diff >= 0 ? "+" : ""}{diff.toLocaleString("ja-JP")})
          </span>
        )}
      </span>
    </div>
  );
};

// ── >3 都市: ランキング表示 (差分ベース) ─────────────

type DiffEntry = { city: string; color: string; diff: number };

function computeDiffs(
  summaries: CitySummary[],
  startKey: "xStart" | "yStart",
  endKey: "xEnd" | "yEnd",
): DiffEntry[] {
  return summaries
    .filter((s) => s[startKey] !== null && s[endKey] !== null)
    .map((s) => ({
      city: s.city,
      color: s.color,
      diff: (s[endKey] as number) - (s[startKey] as number),
    }));
}

const RankingView: React.FC<{
  summaries: CitySummary[];
  itemX: string;
  itemY: string;
}> = ({ summaries, itemX, itemY }) => {
  const diffsX = computeDiffs(summaries, "xStart", "xEnd");
  const diffsY = computeDiffs(summaries, "yStart", "yEnd");

  const topX = [...diffsX].sort((a, b) => b.diff - a.diff).slice(0, 5);
  const bottomX = [...diffsX].sort((a, b) => a.diff - b.diff).slice(0, 5);
  const topY = [...diffsY].sort((a, b) => b.diff - a.diff).slice(0, 5);
  const bottomY = [...diffsY].sort((a, b) => a.diff - b.diff).slice(0, 5);

  return (
    <div style={{ display: "flex", gap: 48 }}>
      <DiffColumn title={`${itemX} 差分 上位5`} items={topX} />
      <DiffColumn title={`${itemX} 差分 下位5`} items={bottomX} />
      <DiffColumn title={`${itemY} 差分 上位5`} items={topY} />
      <DiffColumn title={`${itemY} 差分 下位5`} items={bottomY} />
    </div>
  );
};

const DiffColumn: React.FC<{
  title: string;
  items: DiffEntry[];
}> = ({ title, items }) => {
  const fmtDiff = (n: number) =>
    `${n >= 0 ? "+" : ""}${n.toLocaleString("ja-JP")}円`;

  return (
    <div>
      <div style={{ fontSize: 26, fontWeight: 700, color: TEXT_SECONDARY, marginBottom: 14 }}>
        {title}
      </div>
      {items.map((entry, i) => (
        <div
          key={entry.city}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 8,
          }}
        >
          <span style={{ fontSize: 24, fontWeight: 700, color: "#7a94b8", width: 34 }}>
            {i + 1}.
          </span>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: entry.color,
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 24, color: TEXT_PRIMARY, minWidth: 120 }}>
            {entry.city}
          </span>
          <span
            style={{
              fontSize: 22,
              color: entry.diff >= 0 ? "#4ade80" : "#f87171",
              fontWeight: 600,
            }}
          >
            {fmtDiff(entry.diff)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default Outro;
